import React, { useEffect, useRef, useState } from "react";
import { TruncateDescription, formatDate } from "../utility/Utils";
import apiUrl from "../utility/apiUrl";
import { ArrowUpRight, Calendar, User2 } from "lucide-react";
import useMediaQuery from "../utility/useMediaQuery";
import Carousel from "./ui/Carousel";
import Button from "./ui/Button";
import { SkeletonNewsCard } from "./ui/Skeleton";

const NewsCard = ({
  title,
  description,
  author,
  publishedDate,
  newsImage,
  newsURL,
  featured = false,
  className = "",
}) => {
  const formattedDate = formatDate(publishedDate);
  const imageSrc = newsImage && newsImage !== "N/A" ? newsImage : "/images/college-banner.jpg";

  return (
    <a
      href={newsURL}
      target="_blank"
      rel="noreferrer"
      className={
        "group block overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition " +
        className
      }
      title={title}
    >
      <div
        className={
          "relative w-full bg-slate-100 " +
          (featured ? "aspect-[16/9]" : "h-32 sm:h-36")
        }
      >
        <img
          src={imageSrc}
          alt={title}
          className="h-full w-full object-cover group-hover:scale-[1.02] transition"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src = "/images/college-banner.jpg";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
        <div className="absolute left-4 top-4">
          <span className="inline-flex items-center rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-900">
            Education
          </span>
        </div>
      </div>

      <div className="p-4 md:p-5">
        <div className={"font-bold text-slate-900 group-hover:text-brand-700 transition " + (featured ? "text-lg md:text-xl" : "text-base")}
        >
          {title || "—"}
        </div>
        <div className="mt-2 text-sm text-slate-600">
          <TruncateDescription description={description} />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
          {author && (
            <span className="inline-flex items-center gap-1">
              <User2 className="h-3.5 w-3.5" />
              {author}
            </span>
          )}
          {publishedDate && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {formattedDate}
            </span>
          )}
          <span className="ml-auto inline-flex items-center gap-1 font-semibold text-brand-700">
            Read More
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </a>
  );
};

const News = ({ numberofCards, layout = "default" }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCards, setVisibleCards] = useState(numberofCards);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const rootRef = useRef(null);
  // Index (into visibleNews) of the first article the last "Show More" tap added.
  const revealedFromRef = useRef(null);
  const showMore = () => {
    revealedFromRef.current = visibleCards;
    setVisibleCards(visibleCards + numberofCards);
  };

  // On phones the articles are a swipe carousel, so the extra ones land off to the right of the
  // screen and "Show More" looked like it did nothing. Slide the carousel to the first new
  // article so the tap visibly does something. (The "home" layout shows the first article
  // separately, above the carousel, hence the offset.)
  useEffect(() => {
    const from = revealedFromRef.current;
    if (from === null || !isMobile) return;
    revealedFromRef.current = null;
    const track = rootRef.current?.querySelector(".overflow-x-auto");
    const slide = track?.children?.[layout === "home" ? Math.max(from - 1, 0) : from];
    if (track && slide) {
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: "smooth" });
    }
  }, [visibleCards, isMobile, layout]);

  useEffect(() => {
    const fetchData = async () => {
      fetch(apiUrl + "/api/v1/news/list", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "any",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === "ok") {
            setNews(data.articles);
          }
        })
        .catch((error) => {
          console.error("Error fetching college data:", error);
        })
        .finally(() => setLoading(false));
    };

    fetchData();
  }, []);

  const visibleNews = Array.isArray(news) ? news.slice(0, visibleCards) : [];
  const featured = visibleNews.length > 0 ? visibleNews[0] : null;
  const rest = visibleNews.length > 1 ? visibleNews.slice(1) : [];

  if (loading) {
    return (
      <div className={layout === "list" ? "grid grid-cols-1 gap-4" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"}>
        {[...Array(numberofCards || 4)].map((_, i) => (
          <SkeletonNewsCard key={i} featured={layout === "list"} />
        ))}
      </div>
    );
  }

  return (
    <div ref={rootRef}>
      {layout === "home" ? (
        <>
          {/* 1st row: single featured */}
          {featured && (
            <div className="mb-4">
              <NewsCard
                featured
                className="w-full"
                title={featured.title}
                description={featured.description}
                author={featured.author}
                publishedDate={featured.publishedAt}
                newsImage={featured.urlToImage}
                newsURL={featured.url}
              />
            </div>
          )}

          {/* next rows: 4 per row on lg */}
          {isMobile ? (
            <Carousel showDots>
              {rest.map((article, index) => (
                <NewsCard
                  key={index}
                  title={article.title}
                  description={article.description}
                  author={article.author}
                  publishedDate={article.publishedAt}
                  newsImage={article.urlToImage}
                  newsURL={article.url}
                />
              ))}
            </Carousel>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {rest.map((article, index) => (
                <NewsCard
                  key={index}
                  title={article.title}
                  description={article.description}
                  author={article.author}
                  publishedDate={article.publishedAt}
                  newsImage={article.urlToImage}
                  newsURL={article.url}
                />
              ))}
            </div>
          )}
        </>
      ) : layout === "list" ? (
        <div className="grid grid-cols-1 gap-4">
          {visibleNews.map((article, index) => (
            <NewsCard
              key={index}
              title={article.title}
              description={article.description}
              author={article.author}
              publishedDate={article.publishedAt}
              newsImage={article.urlToImage}
              newsURL={article.url}
              featured
            />
          ))}
        </div>
      ) : layout === "grid4" ? (
        isMobile ? (
          <Carousel showDots>
            {visibleNews.map((article, index) => (
              <NewsCard
                key={index}
                title={article.title}
                description={article.description}
                author={article.author}
                publishedDate={article.publishedAt}
                newsImage={article.urlToImage}
                newsURL={article.url}
              />
            ))}
          </Carousel>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {visibleNews.map((article, index) => (
              <NewsCard
                key={index}
                title={article.title}
                description={article.description}
                author={article.author}
                publishedDate={article.publishedAt}
                newsImage={article.urlToImage}
                newsURL={article.url}
              />
            ))}
          </div>
        )
      ) : (
        /* default layout (keeps CollegeDetails looking compact) */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {featured && (
            <NewsCard
              featured
              className="md:col-span-2"
              title={featured.title}
              description={featured.description}
              author={featured.author}
              publishedDate={featured.publishedAt}
              newsImage={featured.urlToImage}
              newsURL={featured.url}
            />
          )}

          {rest.map((article, index) => (
            <NewsCard
              key={index}
              title={article.title}
              description={article.description}
              author={article.author}
              publishedDate={article.publishedAt}
              newsImage={article.urlToImage}
              newsURL={article.url}
            />
          ))}
        </div>
      )}

      {visibleCards < (news?.length || 0) && (
        <div className="mt-6 text-center">
          <Button onClick={showMore} type="button" variant="primary" className="rounded-xl">
            Show More
          </Button>
        </div>
      )}
    </div>
  );
};

export default News;
